const { getElement } = window.BAO.Utils
const { CarouselElement } = window.BAO.Carousel
const { useAutoplay } = window.BAO.Carousel.plugins

export class announcementBarCarousel extends CarouselElement {
  static get requiredElements () {
    return []
  }

  async connectedCallback () {
    super.connectedCallback()

    this.setupListeners()
    this.setupGwpRefresh()
  }

  // A GWP-dependent bar is rendered against the cart at page load: both the
  // token values and which state block shows (threshold not met / met / all
  // unlocked) are decided server-side. When the cart changes without a reload,
  // re-fetch and re-render the whole bar so the right block shows with fresh
  // values. Only bars flagged data-gwp-refresh (i.e. that depend on the
  // campaign) listen, so ordinary bars never re-fetch.
  setupGwpRefresh () {
    if (!this.hasAttribute('data-gwp-refresh')) return

    this.refreshSection = this.refreshSection.bind(this)
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.refreshSection,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    if (this.refreshSection) {
      window.BAO.eventBus.removeEventListener(
        window.BAO.EVENTS.PUSH_CART.CHANGED,
        this.refreshSection,
      )
    }
  }

  async refreshSection () {
    const { sectionRenderer } = window.BAO
    const html = await sectionRenderer.fetchSingle('announcement-bar')
    const fresh = sectionRenderer
      .convertToDOM(html)
      .querySelector('announcement-bar')

    // No block qualifies now: hide but keep this node (and its listener) so the
    // bar can come back if the customer re-qualifies.
    if (fresh == null) {
      this.style.display = 'none'
      return
    }

    // Swapping the whole element re-runs connectedCallback on the new node,
    // which re-inits the carousel and re-binds this listener; the old node's
    // disconnectedCallback drops the stale one.
    this.replaceWith(this.ownerDocument.importNode(fresh, true))
  }

  setupListeners () {
    if (this.els?.dropdownclose?.exists === true) {
      this.els.dropdownclose.elements.forEach(element => {
        element.addEventListener('click', this.closeDropdown.bind(this))
      })
    }
    if (this.externalEls?.dropdownclose?.exists === true) {
      this.externalEls.dropdownclose.element.addEventListener(
        'click',
        this.closeDropdown.bind(this),
      )
    }

    if (this.els?.dropdownopen?.exists === true) {
      this.els.dropdownopen.elements.forEach(element => {
        element.addEventListener('click', this.openDropdown.bind(this))
      })
    }

    if (this.els?.cart_threshold?.exists === true) {
      const observer = new MutationObserver(() => {
        const pushCartFooter = getElement(
          'div#shopify-section-push-cart-footer',
        )
        const textLabel =
          pushCartFooter &&
          getElement('.prg-ProgressBar_Label', { context: pushCartFooter })

        if (!textLabel?.innerText?.length > 0) return

        this.els.cart_threshold.elements.forEach(element => {
          element.innerText = textLabel?.innerText
        })
      })

      document.addEventListener('DOMContentLoaded', event => {
        const pushCartFooter = getElement(
          'div#shopify-section-push-cart-footer',
        )
        if (!pushCartFooter) return

        observer.observe(pushCartFooter, {
          attributes: true,
          childList: true,
          subtree: true,
        })
      })
    }
  }

  openDropdown (e) {
    document.body.classList.add('hd-AnnouncementBar_Dropdown-active')

    const parentAnnouncement = e.target.closest(
      '.hd-AnnouncementBar_Announcement-hasDropdown',
    )
    if (parentAnnouncement) {
      parentAnnouncement.classList.add(
        'hd-AnnouncementBar_Announcement-openDropdown',
      )
    }
  }

  closeDropdown (e) {
    document.body.classList.remove('hd-AnnouncementBar_Dropdown-active')

    const openDropdown = document.querySelector(
      '.hd-AnnouncementBar_Announcement-openDropdown',
    )
    if (openDropdown) {
      openDropdown.classList.remove(
        'hd-AnnouncementBar_Announcement-openDropdown',
      )
    }
  }

  async initialiseCarousel () {
    if (this.els?.slider?.element == null) return

    this.carousel = await super.initialiseCarousel()
  }

  /** @returns {KeenSliderOptions} */
  get options () {
    return {
      slides: {
        perView: 1,
        spacing: 0,
      },
      loop: true,
      drag: false,
      defaultAnimation: {
        duration: this.durationOfFade,
      },
      detailsChanged: carousel => {
        carousel.slides.forEach((element, idx) => {
          element.style.opacity = carousel.track.details.slides[idx].portion
          const isVisible = idx === carousel.track.details.rel
          element.style.visibility = isVisible ? 'visible' : 'hidden'
        })
      },
      renderMode: 'custom',
      created: carousel => {
        const slider = carousel.container
        slider.setAttribute(`data-initialised`, true)
        setTimeout(() => {
          const slideWithCountdown = carousel.slides.find(slide =>
            slide.classList.contains(
              'hd-AnnouncementBar_Announcement-countdown',
            ),
          )
          slideWithCountdown
            ? (slideWithCountdown.style.position = 'relative')
            : (carousel.slides[0].style.position = 'relative')
        }, 1000)

        const highest = Math.max(
          ...carousel.slides.map(item => item.getBoundingClientRect().height),
        )

        carousel.container.style.height = `${highest}px`
      },
    }
  }

  /** @returns {KeenSliderPlugin[]} */
  get plugins () {
    return [useAutoplay(this.durationTillFade, { pauseOnHover: false })]
  }

  get durationTillFade () {
    return Number(this.dataset.durationTillFade) || 5
  }

  get durationOfFade () {
    return Number(this.dataset.durationOfFade * 100) || 500
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', announcementBarCarousel)
}
